import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lightbulb, X } from "lucide-react";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Accordion, CtaBand, SectionHead, WaitlistCta } from "@/components/marketing/parts";

// The organization's own booking link: the story editor accepts a Calendly
// scheduling URL (organizations.calendly_url), and agents open it from the
// organization's story to pick a time. Compaire does not sync calendars.
const BOOKING_LINK_SENTENCE = "Share your Calendly booking link so agents can book directly.";
const MEETING_BOOKING = "Share your Calendly booking link so agents can choose a time and book directly.";

const PROBLEM_ITEMS = [
  "Cold outreach with low response rates",
  "Expensive recruiting fees",
  "Wasting time on bad-fit candidates",
  "Competitors stealing your best agents",
];

const SOLUTION_ITEMS = [
  "Be found by agents exploring teams and brokerages",
  "Only talk to agents already interested in you",
  "Showcase your culture",
];

const LANDSCAPE: { name: string; rows: [string, string][]; compaire?: boolean }[] = [
  { name: "Traditional Recruiting", rows: [["Quality", "Hit or miss"]] },
  { name: "Job Boards", rows: [["Quality", "Often wrong fit"]] },
  {
    name: "Referrals",
    rows: [
      ["Start", "Hope someone knows someone"],
      ["Quality", "Usually good fit"],
    ],
  },
  {
    name: "Compaire",
    compaire: true,
    rows: [
      ["Discovery", "Organization profiles and search"],
      ["Cost", "$0 during beta"],
    ],
  },
];

const PRODUCT = [
  {
    title: "Help agents find you",
    body: "Help agents exploring their options learn about your organization's culture, training, and support.",
  },
  { title: "Profile analytics", body: "Review profile views, saves and engagement summaries." },
  {
    title: "Tell your team's or brokerage's story",
    body: "Present your organization's culture, compensation models, training, and support.",
  },
  {
    title: "Message interested agents",
    body: "Talk with interested agents directly on Compaire and keep the conversation in one place.",
  },
  {
    // A booking link the organization shares (calendly_url), opened for the
    // agent from the organization's story. Not calendar sync.
    title: "Meeting booking",
    body: MEETING_BOOKING,
  },
];

const FAQS = [
  {
    q: "How is this different from job boards or LinkedIn?",
    a: "Compaire helps agents get to know teams and brokerages before they're ready to make a move. Your profile brings your culture, training, compensation approach and support into one place, giving both sides more context for the first conversation. It can complement your website, referrals and existing recruiting efforts.",
  },
  {
    q: "What if we're already getting referrals?",
    a: "Compaire gives agents another way to discover your organization alongside referrals. Use your profile to explain your culture, training, compensation models, and support.",
  },
  {
    q: "Where does the organization information come from?",
    a: "Teams and brokerages provide and maintain their organization stories. Agents can review the available details and feedback, then ask the organization directly about anything important to their decision.",
  },
  {
    q: "What if we're in a small market?",
    a: "We're launching in pilot markets and expanding based on demand. If your market isn't live yet, joining early gets you first access when we open it — plus founding partner pricing. We track waitlists per market and activate when agent density reaches our threshold.",
  },
  {
    q: "Can we try before committing?",
    a: `Yes. You can create a free organization profile and let agents learn about your culture, training, and support. ${BOOKING_LINK_SENTENCE} No credit card is required. Paid plans are planned for after beta; we'll confirm their included features before paid billing begins.`,
  },
];

// ROI calculator — formula, defaults and ranges unchanged from the previous
// page. The Compaire figure is the regular Pro rate ($499 × 12), not the
// founding rate; a zero or negative difference shows $0 and an em dash.
const COMPAIRE_ANNUAL = 5988;
const fmt = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;

const RoiCalculator = () => {
  const [hires, setHires] = useState(8);
  const [costPerHire, setCostPerHire] = useState(8000);
  const uid = useId();

  const current = hires * costPerHire;
  const savings = current - COMPAIRE_ANNUAL;
  const roiPct = Math.round((savings / COMPAIRE_ANNUAL) * 100);

  const cell = (extra?: React.CSSProperties): React.CSSProperties => ({
    padding: "20px clamp(24px,3cqi,36px)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    ...extra,
  });

  return (
    <div className="mk-card" style={{ overflow: "hidden" }}>
      <div
        className="mk-stack"
        style={{ padding: "clamp(24px,3cqi,36px)", gap: 28, borderBottom: "1px solid var(--mk-border)" }}
      >
        <div className="mk-stack" style={{ gap: 12 }}>
          <div className="mk-field__row">
            <label htmlFor={`${uid}-hires`} style={{ font: "600 var(--mk-text-label-sm)/1.3 var(--mk-sans)" }}>
              Hires per year
            </label>
            <output htmlFor={`${uid}-hires`} style={{ font: "500 var(--mk-text-data-cell)/1 var(--mk-mono)", color: "var(--mk-primary)" }}>
              {hires} hire{hires !== 1 ? "s" : ""}
            </output>
          </div>
          <input
            id={`${uid}-hires`}
            className="mk-range"
            type="range"
            min={1}
            max={40}
            step={1}
            value={hires}
            onChange={(e) => setHires(parseInt(e.target.value, 10))}
          />
        </div>
        <div className="mk-stack" style={{ gap: 12 }}>
          <div className="mk-field__row">
            <label htmlFor={`${uid}-cost`} style={{ font: "600 var(--mk-text-label-sm)/1.3 var(--mk-sans)" }}>
              Avg. cost per hire ($)
            </label>
            <output htmlFor={`${uid}-cost`} style={{ font: "500 var(--mk-text-data-cell)/1 var(--mk-mono)", color: "var(--mk-primary)" }}>
              {fmt(costPerHire)}
            </output>
          </div>
          <input
            id={`${uid}-cost`}
            className="mk-range"
            type="range"
            min={1000}
            max={20000}
            step={500}
            value={costPerHire}
            onChange={(e) => setCostPerHire(parseInt(e.target.value, 10))}
          />
        </div>
      </div>
      <dl
        aria-live="polite"
        style={{ margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,180px),1fr))" }}
      >
        <div
          style={cell({
            borderBottom: "1px solid var(--mk-border)",
            borderRight: "1px solid var(--mk-border)",
            margin: "0 -1px 0 0",
          })}
        >
          <dt style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-muted)" }}>Current annual cost</dt>
          <dd style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{fmt(current)}</dd>
        </div>
        <div style={cell({ borderBottom: "1px solid var(--mk-border)" })}>
          <dt style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-muted)" }}>Compaire annual cost</dt>
          <dd style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{fmt(COMPAIRE_ANNUAL)}</dd>
        </div>
        <div
          style={cell({
            background: "var(--mk-tint)",
            borderRight: "1px solid var(--mk-tint-pressed)",
            margin: "0 -1px 0 0",
          })}
        >
          <dt style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>Your savings</dt>
          <dd style={{ font: "700 var(--mk-text-stat)/1.05 var(--mk-sans)", color: "var(--mk-primary)" }}>
            {savings > 0 ? fmt(savings) : "$0"}
          </dd>
        </div>
        <div style={cell({ background: "var(--mk-tint)" })}>
          <dt style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>ROI</dt>
          <dd style={{ font: "700 var(--mk-text-stat)/1.05 var(--mk-sans)", color: "var(--mk-primary)" }}>
            {savings > 0 ? `↗ ${roiPct.toLocaleString("en-US")}%` : "—"}
          </dd>
        </div>
      </dl>
      <p
        style={{
          padding: "16px clamp(24px,3cqi,36px) 20px",
          borderTop: "1px solid var(--mk-border)",
          font: "400 var(--mk-text-small)/1.4 var(--mk-sans)",
          color: "var(--mk-ink-2)",
          textWrap: "pretty",
        }}
      >
        Illustrative estimate using regular Pro pricing of $499/month ($5,988/year). Beta and founding offers are
        excluded.
      </p>
      <div
        style={{
          padding: "20px clamp(24px,3cqi,36px)",
          display: "flex",
          alignItems: "center",
          borderTop: "1px solid var(--mk-border)",
        }}
      >
        <a href="#how-it-works" className="mk-underline-link">
          See How It Works
        </a>
      </div>
    </div>
  );
};

const ForTeamsLandingPage = () => (
  <MarketingLayout active="teams">
    <CanonicalTags
      path="/teams-brokerages"
      title="For Teams & Brokerages | Tell Your Story | Compaire"
      description="Help agents get to know your team or brokerage. Share your culture, training and support, and start better conversations. Free during beta."
    />

    <section style={{ padding: "clamp(64px,10cqi,144px) 0 clamp(56px,7cqi,96px)" }}>
      <div className="mk-container mk-stack" style={{ alignItems: "flex-start", gap: 28 }}>
        <div className="mk-label">For Teams &amp; Brokerages</div>
        <h1 className="mk-h1" style={{ maxWidth: 980 }}>
          Help the right agents <em className="mk-em">get to know you.</em>
        </h1>
        <p className="mk-lede" style={{ maxWidth: 640 }}>
          Show what everyday work looks like at your team or brokerage. Share your culture, training and support so
          agents arrive at the first conversation with more context. Free during beta.
        </p>
        <WaitlistCta size="lg">Get Early Access</WaitlistCta>
      </div>
    </section>

    <section style={{ padding: "0 0 clamp(72px,9cqi,128px)" }}>
      <div
        className="mk-container"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))", gap: 16 }}
      >
        <div className="mk-card mk-stack" style={{ padding: "clamp(28px,4cqi,44px)", gap: 24 }}>
          <div className="mk-stack" style={{ gap: 10 }}>
            <div className="mk-label mk-label--muted">The Problem</div>
            <h2 style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)" }}>How most teams recruit today</h2>
          </div>
          <ul className="mk-stack" style={{ listStyle: "none", margin: 0, padding: 0, font: "500 var(--mk-text-label)/1.3 var(--mk-sans)" }}>
            {PROBLEM_ITEMS.map((item, i) => (
              <li
                key={item}
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  padding: "14px 0",
                  borderBottom: i < PROBLEM_ITEMS.length - 1 ? "1px solid var(--mk-hair)" : 0,
                }}
              >
                <X size={20} color="var(--mk-muted)" aria-hidden="true" style={{ flex: "none" }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div
          className="mk-stack"
          style={{
            background: "var(--mk-primary)",
            color: "#fff",
            borderRadius: 8,
            padding: "clamp(28px,4cqi,44px)",
            gap: 24,
          }}
        >
          <div className="mk-stack" style={{ gap: 10 }}>
            <div className="mk-label mk-label--onteal">The Solution</div>
            <h2 style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)", color: "#fff" }}>How Compaire teams recruit</h2>
          </div>
          <ul className="mk-stack" style={{ listStyle: "none", margin: 0, padding: 0, font: "500 var(--mk-text-label)/1.3 var(--mk-sans)" }}>
            {SOLUTION_ITEMS.map((item, i) => (
              <li
                key={item}
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  padding: "14px 0",
                  borderBottom: i < SOLUTION_ITEMS.length - 1 ? "1px solid rgba(255,255,255,0.16)" : 0,
                }}
              >
                <Check size={20} color="#fff" aria-hidden="true" style={{ flex: "none" }} />
                {item}
              </li>
            ))}
          </ul>
          <div
            style={{
              marginTop: "auto",
              paddingTop: 24,
              borderTop: "1px solid rgba(255,255,255,0.24)",
              display: "flex",
              alignItems: "baseline",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <span style={{ font: "700 var(--mk-text-stat-lg)/1 var(--mk-sans)", color: "#fff" }}>$0</span>
            <span style={{ font: "500 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-tint)" }}>
              Compaire cost during beta
            </span>
          </div>
        </div>
      </div>
    </section>

    <section className="mk-section mk-section--rule">
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,440px),1fr))",
          gap: "clamp(40px,6cqi,96px)",
          alignItems: "start",
        }}
      >
        <SectionHead label="ROI" title="Calculate your recruiting savings." maxWidth={480} />
        <RoiCalculator />
      </div>
    </section>

    <section className="mk-section mk-section--rule">
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,56px)" }}>
        <SectionHead label="Your recruiting options" title="Recruiting is broken. Here's why." />
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,250px),1fr))", gap: 12 }}
        >
          {LANDSCAPE.map((col) => (
            <div
              key={col.name}
              style={{
                background: "#fff",
                border: col.compaire ? "2px solid var(--mk-primary)" : "1px solid var(--mk-border)",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <h3
                style={
                  col.compaire
                    ? { padding: "17px 20px", background: "var(--mk-primary)", color: "#fff", font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }
                    : { padding: "18px 20px", borderBottom: "1px solid var(--mk-border)", font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }
                }
              >
                {col.name}
              </h3>
              <dl style={{ margin: 0, padding: "4px 20px 8px", font: "400 var(--mk-text-small)/1.4 var(--mk-sans)" }}>
                {col.rows.map(([term, desc], i) => (
                  <div
                    key={term}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "11px 0",
                      borderBottom: i < col.rows.length - 1 ? "1px solid var(--mk-hair)" : 0,
                    }}
                  >
                    <dt style={{ color: "var(--mk-muted)" }}>{term}</dt>
                    <dd
                      style={{
                        textAlign: "right",
                        fontWeight: col.compaire ? 600 : 500,
                        color: col.compaire ? "var(--mk-primary)" : undefined,
                      }}
                    >
                      {desc}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        <p style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>
          Help agents find you before you reach out.
        </p>
      </div>
    </section>

    <section
      id="how-it-works"
      className="mk-section mk-section--white mk-section--rule"
      style={{ scrollMarginTop: 96 }}
    >
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,64px)" }}>
        <SectionHead label="How It Works" title="Get discovered by the right agents." maxWidth={640} />
        <ol className="mk-steps">
          <li>
            <span className="mk-steps__num">01</span>
            <h3>Create Your Profile</h3>
            <p>Showcase compensation, culture, training, and support. Upload photos, videos, and testimonials.</p>
          </li>
          <li>
            <span className="mk-steps__num">02</span>
            <h3>Get Discovered</h3>
            <p>
              Agents in your market find you through search. They see your Fit Score, read reviews, and watch videos.
            </p>
            <span
              style={{
                marginTop: 4,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                font: "500 var(--mk-text-label-sm)/1.3 var(--mk-sans)",
                color: "var(--mk-primary)",
              }}
            >
              <Lightbulb size={18} aria-hidden="true" style={{ flex: "none", marginTop: 1 }} />
              You choose how to present your team or brokerage.
            </span>
          </li>
          <li>
            <span className="mk-steps__num">03</span>
            <h3>Connect with Interested Agents</h3>
            <p>Connect with agents who choose to reach out.</p>
          </li>
        </ol>
        <div>
          <WaitlistCta>Get Early Access</WaitlistCta>
        </div>
      </div>
    </section>

    <section className="mk-section mk-section--rule">
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,56px)" }}>
        <SectionHead label="Product" title="Give agents a clearer picture of your organization." />
        <div className="mk-hairgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,320px),1fr))" }}>
          {PRODUCT.map((item) => (
            <div key={item.title} className="mk-stack" style={{ padding: "clamp(24px,3cqi,36px)", gap: 12 }}>
              <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{item.title}</h3>
              <p className="mk-body">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section
      className="mk-section--white mk-section--rule"
      style={{ padding: "clamp(56px,7cqi,96px) 0" }}
    >
      <div
        className="mk-container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "24px 48px",
          flexWrap: "wrap",
        }}
      >
        <div className="mk-stack" style={{ gap: 16, maxWidth: 640 }}>
          <div className="mk-label">Pricing</div>
          <h2 className="mk-h2 mk-h2--sm">Simple, transparent pricing.</h2>
          <p className="mk-body">
            Free during beta. Founding plans from $349/month when you're ready to grow.
          </p>
        </div>
        <Link to="/pricing" className="mk-btn mk-btn--outline">
          View Pricing →
        </Link>
      </div>
    </section>

    <section className="mk-section mk-section--rule">
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,360px),1fr))",
          gap: "clamp(32px,5cqi,80px)",
          alignItems: "start",
        }}
      >
        <SectionHead
          label="FAQ"
          title="Common questions from team leaders & brokerages."
          titleClass="mk-h2 mk-h2--sm"
        />
        <Accordion items={FAQS} />
      </div>
    </section>

    <CtaBand
      title={
        <>
          Tell your story. <em>Start a better conversation.</em>
        </>
      }
      body="Help agents understand your culture, training and support before they choose to reach out."
      cta={
        <WaitlistCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
          Get Early Access
        </WaitlistCta>
      }
    />
  </MarketingLayout>
);

export default ForTeamsLandingPage;
